import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import axios from "axios";
import { 
  LayoutDashboard, Users, Calendar, CreditCard, Bell, LogOut, 
  Menu, X, TrendingUp, Clock, CheckCircle, AlertCircle, FileText, Edit3, BarChart3, UserCog, Image, Eye
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import AdminSidebar from "@/components/AdminSidebar";
import { useNotificationCount } from "@/hooks/useNotificationCount";
import { useAppSettings } from "@/context/SettingsContext";

const API = "/api";

const Dashboard = () => {
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const notificationCount = useNotificationCount();
  const { subscriptionsEnabled } = useAppSettings();
  const [stats, setStats] = useState(null);
  const [recentJobs, setRecentJobs] = useState([]);
  const [pendingQuotes, setPendingQuotes] = useState(0);
  const [visitorStats, setVisitorStats] = useState(null);
  const [loading, setLoading] = useState(true);

  
  const LOGO_URL = "https://customer-assets.emergentagent.com/job_a07fe825-978d-42af-ad0d-66b05bf66e9f/artifacts/6h7nm3ap_Photoroom_20260215_225825.png";

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, jobsRes, quotesRes, visitorsRes] = await Promise.all([
          axios.get(`${API}/dashboard/stats`, { withCredentials: true }),
          axios.get(`${API}/jobs`, { withCredentials: true }),
          axios.get(`${API}/quotes`, { withCredentials: true }).catch(() => ({ data: [] })),
          axios.get(`${API}/analytics/visitors`, { withCredentials: true }).catch(() => ({ data: null })),
        ]);
        setStats(statsRes.data);
        setRecentJobs(jobsRes.data.slice(0, 5));
        setPendingQuotes(quotesRes.data.filter(q => q.status === "pending").length);
        setVisitorStats(visitorsRes.data);
      } catch (error) {
        console.error("Failed to fetch dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const getStatusIcon = (status) => {
    switch (status) {
      case "completed":
        return <CheckCircle className="w-4 h-4 text-[#39FF14]" />;
      case "in_progress":
        return <Clock className="w-4 h-4 text-yellow-400" />;
      case "cancelled":
        return <AlertCircle className="w-4 h-4 text-red-400" />;
      default:
        return <Clock className="w-4 h-4 text-blue-400" />;
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex">
      <AdminSidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} notificationCount={notificationCount} />

      {/* Main Content */}
      <main className="flex-1 min-h-screen">
        {/* Top Bar */}
        <header className="sticky top-0 z-30 bg-[#0A0A0A]/80 backdrop-blur-lg border-b border-[#27272A] p-4 lg:p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button 
                className="lg:hidden text-[#A1A1AA] hover:text-white"
                onClick={() => setSidebarOpen(true)}
              >
                <Menu className="w-6 h-6" />
              </button>
              <div>
                <h1 className="font-heading text-2xl lg:text-3xl font-bold text-white uppercase">{t("admin.dashboard", "Dashboard")}</h1>
                <p className="text-[#A1A1AA] text-sm">{t("dashboard.welcomeBack", "Welcome back")}, {user?.name?.split(" ")[0]}</p>
              </div>
            </div>
          </div>
        </header>

        {/* Dashboard Content */}
        <div className="p-4 lg:p-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-12 h-12 border-4 border-[#39FF14] border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : (
            <>
              {/* Stats Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
                {/* Page Visitors - NEW */}
                <div data-testid="stat-visitors" className="bg-[#121212] border border-[#27272A] rounded-xl p-6 hover:border-blue-500/50 transition-colors">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 bg-blue-900/50 rounded-lg flex items-center justify-center">
                      <Eye className="w-6 h-6 text-blue-400" />
                    </div>
                    <span className="text-blue-400 text-sm font-medium">
                      {t("dashboard.today", "Today")}: {visitorStats?.today?.visits || 0}
                    </span>
                  </div>
                  <p className="text-[#A1A1AA] text-sm uppercase tracking-wide mb-1">Total Visitors</p>
                  <p className="text-3xl font-bold text-blue-400">{visitorStats?.total_visits?.toLocaleString() || 0}</p>
                  <p className="text-[#A1A1AA] text-xs mt-1">
                    This week: {visitorStats?.weekly?.visits || 0}
                  </p>
                </div>

                <div data-testid="stat-customers" className="bg-[#121212] border border-[#27272A] rounded-xl p-6 hover:border-[#39FF14]/50 transition-colors">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 bg-[#064E3B] rounded-lg flex items-center justify-center">
                      <Users className="w-6 h-6 text-[#39FF14]" />
                    </div>
                    <TrendingUp className="w-5 h-5 text-[#39FF14]" />
                  </div>
                  <p className="text-[#A1A1AA] text-sm uppercase tracking-wide mb-1">{t("dashboard.totalCustomers", "Total Customers")}</p>
                  <p className="text-3xl font-bold text-white">{stats?.total_customers || 0}</p>
                  <div className="flex gap-3 mt-3">
                    <span className="text-[#39FF14] text-xs font-medium px-2 py-0.5 bg-[#39FF14]/10 rounded-full">
                      {stats?.active_customers || 0} Active
                    </span>
                    <span className="text-yellow-400 text-xs font-medium px-2 py-0.5 bg-yellow-400/10 rounded-full">
                      {stats?.pending_customers || 0} Pending
                    </span>
                  </div>
                </div>

                <div data-testid="stat-jobs" className="bg-[#121212] border border-[#27272A] rounded-xl p-6 hover:border-[#39FF14]/50 transition-colors">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 bg-[#064E3B] rounded-lg flex items-center justify-center">
                      <Calendar className="w-6 h-6 text-[#39FF14]" />
                    </div>
                    <span className="text-[#39FF14] text-sm font-medium">{t("dashboard.today", "Today")}: {stats?.jobs_today || 0}</span>
                  </div>
                  <p className="text-[#A1A1AA] text-sm uppercase tracking-wide mb-1">{t("dashboard.totalJobs", "Total Jobs")}</p>
                  <p className="text-3xl font-bold text-white">{stats?.total_jobs || 0}</p>
                  <div className="flex gap-3 mt-3">
                    <span className="text-blue-400 text-xs font-medium px-2 py-0.5 bg-blue-400/10 rounded-full">
                      {stats?.jobs_scheduled || 0} Scheduled
                    </span>
                    <span className="text-[#39FF14] text-xs font-medium px-2 py-0.5 bg-[#39FF14]/10 rounded-full">
                      {stats?.jobs_completed || 0} Completed
                    </span>
                  </div>
                </div>

                {subscriptionsEnabled && <div data-testid="stat-subscriptions" className="bg-[#121212] border border-[#27272A] rounded-xl p-6 hover:border-[#39FF14]/50 transition-colors">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 bg-[#064E3B] rounded-lg flex items-center justify-center">
                      <CreditCard className="w-6 h-6 text-[#39FF14]" />
                    </div>
                  </div>
                  <p className="text-[#A1A1AA] text-sm uppercase tracking-wide mb-1">{t("dashboard.activeSubscriptions", "Active Subscriptions")}</p>
                  <p className="text-3xl font-bold text-white">{stats?.active_subscriptions || 0}</p>
                  <div className="flex gap-3 mt-3">
                    <span className="text-[#39FF14] text-xs font-medium px-2 py-0.5 bg-[#39FF14]/10 rounded-full">
                      {stats?.monthly_subscriptions || 0} Monthly
                    </span>
                    <span className="text-yellow-400 text-xs font-medium px-2 py-0.5 bg-yellow-400/10 rounded-full">
                      {stats?.yearly_subscriptions || 0} Yearly
                    </span>
                  </div>
                </div>}

                <div data-testid="stat-revenue" className="bg-[#121212] border border-[#39FF14] rounded-xl p-6 shadow-[0_0_15px_rgba(57,255,20,0.1)]">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 bg-[#39FF14] rounded-lg flex items-center justify-center">
                      <TrendingUp className="w-6 h-6 text-black" />
                    </div>
                  </div>
                  <p className="text-[#A1A1AA] text-sm uppercase tracking-wide mb-1">{t("dashboard.totalRevenue", "Total Revenue")}</p>
                  <p className="text-3xl font-bold text-[#39FF14]">${stats?.total_revenue?.toFixed(2) || "0.00"}</p>
                </div>
              </div>

              {/* Bento Grid Layout */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Recent Jobs */}
                <div className="lg:col-span-2 bg-[#121212] border border-[#27272A] rounded-xl p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="font-heading text-xl font-bold text-white uppercase">{t("dashboard.recentJobs", "Recent Jobs")}</h2>
                    <Link to="/schedule" className="text-[#39FF14] text-sm hover:underline">{t("common.viewAll", "View All")}</Link>
                  </div>
                  {recentJobs.length === 0 ? (
                    <div className="text-center py-8">
                      <Calendar className="w-12 h-12 text-[#27272A] mx-auto mb-4" />
                      <p className="text-[#A1A1AA]">{t("dashboard.noJobs", "No jobs scheduled yet")}</p>
                      <Link to="/schedule">
                        <Button className="mt-4 bg-[#39FF14] text-black hover:bg-[#32D711]">
                          {t("dashboard.scheduleFirstJob", "Schedule First Job")}
                        </Button>
                      </Link>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {recentJobs.map((job) => (
                        <div 
                          key={job.job_id}
                          data-testid={`job-${job.job_id}`}
                          className="flex items-center gap-4 p-4 bg-[#0A0A0A] rounded-lg border border-[#27272A] hover:border-[#39FF14]/30 transition-colors"
                        >
                          {getStatusIcon(job.status)}
                          <div className="flex-1 min-w-0">
                            <p className="text-white font-medium truncate">{job.customer_name}</p>
                            <p className="text-[#A1A1AA] text-sm truncate">{job.customer_address}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-white font-medium">{formatDate(job.scheduled_date)}</p>
                            <p className="text-[#A1A1AA] text-sm">{job.scheduled_time}</p>
                          </div>
                          <p className="text-[#39FF14] font-bold">${job.final_price?.toFixed(2)}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Job Status Overview */}
                <div className="bg-[#121212] border border-[#27272A] rounded-xl p-6">
                  <h2 className="font-heading text-xl font-bold text-white uppercase mb-6">{t("dashboard.jobStatus", "Job Status")}</h2>
                  <div className="space-y-4">
                    {/* Awaiting Quote - Links to Quotes page */}
                    <Link 
                      to="/quotes"
                      className="flex items-center justify-between p-3 bg-[#0A0A0A] rounded-lg hover:bg-[#1A1A1A] transition-colors cursor-pointer border border-transparent hover:border-yellow-400/30"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-3 h-3 rounded-full bg-yellow-400 animate-pulse"></div>
                        <span className="text-[#EDEDED]">{t("quotes.awaiting", "Awaiting Quote")}</span>
                      </div>
                      <span className="text-yellow-400 font-bold">{pendingQuotes}</span>
                    </Link>
                    <div className="flex items-center justify-between p-3 bg-[#0A0A0A] rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-3 h-3 rounded-full bg-blue-400"></div>
                        <span className="text-[#EDEDED]">{t("dashboard.scheduled", "Scheduled")}</span>
                      </div>
                      <span className="text-white font-bold">{stats?.jobs_by_status?.scheduled || 0}</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-[#0A0A0A] rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                        <span className="text-[#EDEDED]">{t("dashboard.inProgress", "In Progress")}</span>
                      </div>
                      <span className="text-white font-bold">{stats?.jobs_by_status?.in_progress || 0}</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-[#0A0A0A] rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-3 h-3 rounded-full bg-[#39FF14]"></div>
                        <span className="text-[#EDEDED]">{t("dashboard.completed", "Completed")}</span>
                      </div>
                      <span className="text-white font-bold">{stats?.jobs_by_status?.completed || 0}</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-[#0A0A0A] rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-3 h-3 rounded-full bg-red-400"></div>
                        <span className="text-[#EDEDED]">{t("dashboard.cancelled", "Cancelled")}</span>
                      </div>
                      <span className="text-white font-bold">{stats?.jobs_by_status?.cancelled || 0}</span>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
